/** A workspace's settings page and the agents' one-click installs. */
export const workspace = {
  planActive: 'Team plan is active — welcome aboard',
  checkoutCanceled: 'Checkout canceled — nothing was charged',
  needsPlan: '"{name}" needs a plan first.',
  thisWorkspace: 'This workspace',
  notFound: 'Workspace not found',
  notFoundBody: 'It may have been deleted, or you are no longer a member.',
  backToCanvases: 'Back to canvases',
  workspace: 'Workspace',
  people: 'People',
  fonts: 'Fonts',
  planBilling: 'Plan & billing',
  billing: 'Billing',
  general: 'General',
  breadcrumb: 'Breadcrumb',
  home: 'Home',
  memberCount: { one: '{count} person', other: '{count} people' },
  canvasCount: { one: '{count} canvas', other: '{count} canvases' },
  selfHostedIncluded: 'Self-hosted, included',
  noPlanYet: 'No plan yet.',
  noPlanBody: 'Inviting people and adding canvases waits until this workspace is on Team.',
  canceledBody: ' Your existing canvases stay open to everyone in it.',
  reactivate: 'Reactivate',
  choosePlan: 'Choose a plan',
  paymentFailed: 'The last payment failed.',
  paymentFailedBody: 'Stripe is retrying; update the card in the billing portal to keep the plan.',

  statusInactive: 'No plan yet',
  statusTrialing: 'Trial',
  statusActive: 'Active',
  statusPastDue: 'Payment failed',
  statusCanceled: 'Canceled',

  invitedMember: '{name} is in',
  inviteEmailed: 'Invite emailed to {email}',
  invitePending: '{email} joins when they sign up with that email',
  inviteFailed: 'Couldn’t invite',
  roleChangeFailed: 'Couldn’t change the role',
  removeFailed: 'Couldn’t remove',
  revokeFailed: 'Couldn’t revoke the invite',
  peopleDescription:
    'Everyone here opens every canvas in the workspace. Admins invite and remove people; the owner holds the billing.',
  oneSeatEach: ' Each person is one seat.',
  inviteByEmail: 'Invite by email',
  emailToInvite: 'Email to invite',
  role: 'Role',
  inviting: 'Inviting…',
  invite: 'Invite',
  roleOwner: 'owner',
  roleAdmin: 'admin',
  roleMember: 'member',
  roleOf: 'Role of {name}',
  leave: 'Leave',
  remove: 'Remove',
  you: '{name} (you)',
  emailJoined: '{email} · joined {time}',
  waitingToSignUp: 'Waiting to sign up',
  invitedBy: '· {role} · invited by {name}',
  revoke: 'Revoke',
  leaveTitle: 'Leave “{name}”?',
  removeTitle: 'Remove {name}?',
  leaveDescription: 'You lose access to every canvas in this workspace except the ones you own.',
  removeDescription:
    'They lose access to every canvas in this workspace except the ones they own. Their seat is released.',
  leaveWorkspace: 'Leave workspace',
  member: 'Member',
  admin: 'Admin',

  portalFailed: 'Couldn’t open the billing portal',
  upToDate: 'Up to date with Stripe',
  refreshFailed: 'Couldn’t refresh',
  billingDescription:
    'Team is billed per seat: every person in the workspace, monthly or yearly. Seats follow the people list — add someone and the next invoice grows, remove them and it shrinks.',
  billingDisabledDescription:
    'Billing isn’t configured on this server. Workspaces are included on self-hosted doop; there is nothing to pay for here.',
  plan: 'Plan',
  planTeam: 'Team',
  planPersonal: 'Personal',
  planSelfHosted: 'Self-hosted',
  endsOn: 'ends {date}',
  seats: 'Seats',
  seatsMath: '{seats} × {price} =',
  perMonth: 'per month',
  perYear: 'per year',
  seatsCatchUp: {
    one: '({count} person now — Stripe catches up on the next invoice)',
    other: '({count} people now — Stripe catches up on the next invoice)',
  },
  ends: 'Ends',
  renews: 'Renews',
  manage: 'Manage',
  reactivateTeam: 'Reactivate on Team',
  opening: 'Opening…',
  manageOnStripe: 'Manage billing on Stripe',
  refresh: 'Refresh',
  stripePortalNote: 'Cards, invoices, cancelling — all on Stripe’s portal.',
  ownerHoldsBilling: 'The owner holds the billing; only they can change the plan.',

  renamed: 'Renamed',
  renameFailed: 'Couldn’t rename',
  deleteFailed: 'Couldn’t delete the workspace',
  generalDescription: 'The name everyone sees in their sidebar and on canvases filed here.',
  name: 'Name',
  workspaceName: 'Workspace name',
  created: 'Created',
  deleteWorkspace: 'Delete workspace',
  deleteDescription:
    'Every canvas goes back to the personal space of whoever owns it — nothing is deleted. People lose access to canvases that aren’t theirs.',
  deleteCancelsTeam: ' The Team subscription is canceled immediately.',
  deleteNamed: 'Delete “{name}”',
  deleteTitle: 'Delete “{name}”?',
  deleteConfirmDescription: 'Canvases return to their owners and the subscription ends now. This can’t be undone.',

  /* Codex */
  codexManual: 'Run this in a terminal with Codex CLI installed:',
  codexAdding: 'Adding Doop…',
  codexAdded: 'Added to Codex',
  codexAdd: 'Add to Codex',
  codexConfigured: 'Doop is configured. Open a new Codex chat, then sign in to Doop when prompted.',
  codexUpdateDesktop: 'Update Doop Desktop to add Codex directly, or use the manual setup below.',
  openDesktop: 'Open Doop Desktop',
  codexNeedsDesktop:
    'Requires the updated Doop desktop app. Click Add to Codex there to finish setup. If the app doesn’t open, use the manual setup below.',

  /* Claude Desktop */
  claudeOpening: 'Opening Claude…',
  claudeOpenAgain: 'Open Claude again',
  claudeAdd: 'Add to Claude',
  claudeNeedsDesktop:
    'Open this server in an updated Doop desktop app to add Claude directly. In a browser, you can use the manual installation below.',
  claudeConfirm: 'Confirm the installation in Claude, then approve the Doop sign-in.',
  installManually: 'Install manually',
  downloadExtension: 'Download extension',
  claudeManualOpen: ', then open it with Claude Desktop. Set',
  claudeManualTo: 'to',
  claudeManualFallback: '. If the file doesn’t open, use Claude’s',

  /* Cursor */
  cursorAdd: 'Add to Cursor',
  connectionHelp: 'Connection help',
  cursorHelpBefore: 'If Cursor does not open, open its',
  cursorHelpMiddle: 'panel and add a custom MCP server. Merge this',
  cursorHelpAfter:
    'entry into your MCP configuration, keeping any existing servers. If Doop is already connected through a plugin, use that connection.',
} as const
